import { useId, useState, type FormEvent } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { formatarDecimal } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import type { TurnoDaApi } from './api';
import { explicarFalha, useGravarTurnos, useTurnos } from './consultas';
import { fraseDoErro } from './texto';
import { DIAS_DA_SEMANA, horasDoTurno, horasPorDia, turnoAcrescentado } from './turno';

/** As regras do editor, uma por campo — é o que deixa validar ao sair dele (p.16). */
const MENSAGEM_DO_MOTIVO = 'Diga por que o turno muda: a troca muda a disponibilidade de todo período consultado.';
const MENSAGEM_DO_NOME = 'Todo turno precisa de nome, por exemplo "Diurno".';

const mensagemDoMotivo = (motivo: string) => (motivo.trim() === '' ? MENSAGEM_DO_MOTIVO : null);
const mensagemDoNome = (nome: string) => (nome.trim() === '' ? MENSAGEM_DO_NOME : null);

interface Erros {
  readonly motivo: string | null;
  /** Por posição da linha no rascunho. */
  readonly nomes: Readonly<Record<number, string>>;
}
const SEM_ERRO: Erros = { motivo: null, nomes: {} };

/**
 * O turno da empresa — decisão 19 do dono: "parametrizável em configurações de
 * cada empresa". É a janela programada, e é dela que a disponibilidade tira o
 * denominador.
 *
 * A semana se grava inteira, com motivo: trocar o turno muda a disponibilidade
 * de todo período consultado, e a trilha guarda a semana anterior.
 */
export function TurnoDaEmpresa() {
  const base = useId();
  const sessao = useSessao();
  const podeGerir = (sessao.data?.permissoes ?? []).includes('turno.gerir');
  const turnos = useTurnos();
  const gravar = useGravarTurnos();
  const [rascunho, setRascunho] = useState<TurnoDaApi[] | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Erros>(SEM_ERRO);
  /* A recusa do SERVIDOR, que não é de campo nenhum: essa sim fica em bloco. */
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);

  const atuais = turnos.data ?? [];
  const editando = rascunho !== null;
  const linhas = rascunho ?? atuais;
  const porDia = horasPorDia(linhas);
  const semTurno = !turnos.isPending && !turnos.isError && atuais.length === 0;
  const mostrarSemana = editando || (!semTurno && !turnos.isError);

  function mudar(i: number, campo: Partial<TurnoDaApi>) {
    setRascunho((r) => (r ?? []).map((t, j) => (j === i ? { ...t, ...campo } : t)));
  }

  function conferirNome(i: number, nome: string) {
    const mensagem = mensagemDoNome(nome);
    setErros((atual) => {
      const nomes = Object.fromEntries(Object.entries(atual.nomes).filter(([j]) => Number(j) !== i));
      return { ...atual, nomes: mensagem === null ? nomes : { ...nomes, [i]: mensagem } };
    });
  }

  function editar(inicio: TurnoDaApi[]) {
    setRascunho(inicio);
    setErros(SEM_ERRO);
    setRecusa(null);
  }

  function cancelar() {
    setRascunho(null);
    setErros(SEM_ERRO);
    setRecusa(null);
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!rascunho) return;

    /* No envio, revalida tudo, e cada erro vai para baixo do SEU campo — o nome
       na linha dele, o motivo no motivo. Antes os dois saíam num parágrafo
       preto acima da barra, longe do campo a que se referiam. */
    const nomes: Record<number, string> = {};
    for (const [i, t] of rascunho.entries()) {
      const mensagem = mensagemDoNome(t.nome);
      if (mensagem !== null) nomes[i] = mensagem;
    }
    const achados: Erros = { motivo: mensagemDoMotivo(motivo), nomes };
    setErros(achados);

    const primeiraLinha = Object.keys(nomes).map(Number).sort((a, b) => a - b)[0];
    if (primeiraLinha !== undefined) {
      document.getElementById(`${base}-${String(primeiraLinha)}-nome`)?.focus();
      return;
    }
    if (achados.motivo !== null) {
      document.getElementById(`${base}-motivo`)?.focus();
      return;
    }

    setRecusa(null);
    gravar.mutate(
      { turnos: rascunho, motivo: motivo.trim() },
      {
        onSuccess: () => {
          setRascunho(null);
          setMotivo('');
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <section className="cartao flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Turno da empresa</h2>
        <p className="txt-apoio text-tinta-secundaria">
          As horas que cada máquina deveria trabalhar em cada dia da semana. É o denominador da disponibilidade: sem ele,
          o sistema sabe quantas horas foram apontadas e não quantas eram devidas.
        </p>
      </div>

      {/* A SEMANA RESUMIDA: é o que quem não edita precisa ler. Sete caixas
          numa linha só a partir de 768 px; abaixo disso, quatro por linha — em
          390 px as sete ficavam com 44 px e "9,0 h" quebrava (R01, p.28). Sem
          turno cadastrado ela some: sete traços repetiriam o vazio de baixo. */}
      {mostrarSemana ? (
        <dl className="grid grid-cols-4 gap-2 min-[768px]:grid-cols-7">
          {DIAS_DA_SEMANA.map((dia, i) => (
            <div key={dia} className="flex flex-col gap-1 rounded-controle border border-borda-sutil p-2">
              <dt className="txt-legenda font-normal text-tinta-secundaria">{dia}</dt>
              <dd className="numero whitespace-nowrap text-right text-tinta">
                {turnos.isPending && !editando ? (
                  /* Esqueleto na linha do valor (p.32), na altura do texto: o bloco
                     não pula quando o turno chega. */
                  <span aria-hidden className="inline-block h-3 w-8 esqueleto align-middle" />
                ) : porDia[i] ? (
                  /* Número e unidade separados, a unidade em cinza (p.12). */
                  <Medida valor={formatarDecimal(porDia[i], 1)} unidade="h" />
                ) : (
                  '—'
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {/* Uma região de status para a semana inteira; as barras são decorativas. */}
      {turnos.isPending && !editando ? (
        <span role="status" aria-busy="true" className="sr-only">
          Carregando o turno da empresa
        </span>
      ) : null}

      {turnos.isError ? (
        <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{fraseDoErro(explicarFalha(turnos.error).erro)}</span>
          <span className="txt-apoio text-tinta-secundaria">{explicarFalha(turnos.error).comoResolver}</span>
        </div>
      ) : null}

      {semTurno && !editando ? (
        /* PRIMEIRO USO (p.23): o vazio oferece a ação, e não um "Alterar turno"
           solto para um cadastro que ainda não existe. */
        <EstadoVazio
          titulo="Nenhum turno cadastrado"
          descricao={
            podeGerir
              ? 'Cadastre o turno da empresa para a tela de operação calcular a disponibilidade.'
              : 'Peça a quem administra o sistema para cadastrar o turno da empresa.'
          }
          {...(podeGerir
            ? {
                acao: (
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      editar([]);
                    }}
                  >
                    Cadastrar turno
                  </Botao>
                ),
              }
            : {})}
        />
      ) : null}

      {editando ? (
        <form onSubmit={enviar} className="flex flex-col gap-4">
          {rascunho.map((t, i) => (
            <div key={i} className="flex flex-wrap items-end gap-4 border-b border-borda-sutil pb-4">
              <Campo id={`${base}-${String(i)}-dia`} rotulo="Dia">
                <select
                  id={`${base}-${String(i)}-dia`}
                  className="controle"
                  value={t.diaSemana}
                  onChange={(e) => {
                    mudar(i, { diaSemana: Number(e.target.value) });
                  }}
                >
                  {DIAS_DA_SEMANA.map((dia, d) => (
                    <option key={dia} value={d}>
                      {dia}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo id={`${base}-${String(i)}-nome`} rotulo="Nome" erro={erros.nomes[i] ?? null}>
                <input
                  id={`${base}-${String(i)}-nome`}
                  className="controle"
                  maxLength={40}
                  value={t.nome}
                  {...(erros.nomes[i]
                    ? { 'aria-invalid': true, 'aria-describedby': `${base}-${String(i)}-nome-erro` }
                    : {})}
                  onChange={(e) => {
                    mudar(i, { nome: e.target.value });
                    setErros((atual) => ({
                      ...atual,
                      nomes: Object.fromEntries(Object.entries(atual.nomes).filter(([j]) => Number(j) !== i)),
                    }));
                  }}
                  onBlur={(e) => {
                    conferirNome(i, e.target.value);
                  }}
                />
              </Campo>
              <Campo id={`${base}-${String(i)}-ini`} rotulo="Início">
                <input id={`${base}-${String(i)}-ini`} type="time" className="controle" value={t.inicio} onChange={(e) => { mudar(i, { inicio: e.target.value }); }} />
              </Campo>
              <Campo id={`${base}-${String(i)}-fim`} rotulo="Fim">
                <input id={`${base}-${String(i)}-fim`} type="time" className="controle" value={t.fim} onChange={(e) => { mudar(i, { fim: e.target.value }); }} />
              </Campo>
              <Campo id={`${base}-${String(i)}-int`} rotulo="Intervalo (min)">
                <input
                  id={`${base}-${String(i)}-int`}
                  type="number"
                  min={0}
                  className="controle numero"
                  value={t.intervaloMin}
                  onChange={(e) => {
                    mudar(i, { intervaloMin: Math.max(0, Number(e.target.value) || 0) });
                  }}
                />
              </Campo>
              <span className="txt-apoio whitespace-nowrap pb-2 text-tinta-secundaria">
                <Medida valor={formatarDecimal(horasDoTurno(t), 1)} unidade="h" />
              </span>
              <Botao
                nivel="terciario"
                type="button"
                onClick={() => {
                  setRascunho((r) => (r ?? []).filter((_, j) => j !== i));
                  /* As posições andam: o erro de nome da linha de baixo não pode
                     ficar pendurado na de cima. O envio confere de novo. */
                  setErros((atual) => ({ ...atual, nomes: {} }));
                }}
              >
                Tirar turno
              </Botao>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Botao
              nivel="secundario"
              type="button"
              onClick={() => {
                /* Repete o de cima no dia seguinte (`turnoAcrescentado`). */
                setRascunho((r) => [...(r ?? []), turnoAcrescentado(r?.at(-1))]);
              }}
            >
              Acrescentar turno
            </Botao>
            <Botao
              nivel="secundario"
              type="button"
              onClick={() => {
                setRascunho(
                  [1, 2, 3, 4, 5].map((d) => ({ nome: 'Diurno', diaSemana: d, inicio: '07:00', fim: '17:00', intervaloMin: 60 })),
                );
                setErros((atual) => ({ ...atual, nomes: {} }));
              }}
            >
              Preencher dias úteis
            </Botao>
          </div>
          <Campo id={`${base}-motivo`} rotulo="Motivo da troca" erro={erros.motivo}>
            <input
              id={`${base}-motivo`}
              className="controle w-full"
              maxLength={400}
              value={motivo}
              {...(erros.motivo ? { 'aria-invalid': true, 'aria-describedby': `${base}-motivo-erro` } : {})}
              onChange={(e) => {
                setMotivo(e.target.value);
                setErros((atual) => ({ ...atual, motivo: null }));
              }}
              onBlur={(e) => {
                const mensagem = mensagemDoMotivo(e.target.value);
                setErros((atual) => ({ ...atual, motivo: mensagem }));
              }}
            />
          </Campo>
          {recusa === null ? null : (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
              <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
              <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
            </div>
          )}
          {/* Barra de ações (R01). O editor é de linhas repetidas, não de
              campos soltos: a grade da R01 não se aplica às linhas. */}
          <div className="barra-de-acoes">
            <Botao nivel="secundario" type="button" onClick={cancelar}>
              Cancelar
            </Botao>
            <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
              Gravar turno
            </Botao>
          </div>
        </form>
      ) : podeGerir && atuais.length > 0 ? (
        <div className="flex justify-end">
          <Botao
            nivel="secundario"
            onClick={() => {
              editar(atuais.map((t) => ({ ...t })));
            }}
          >
            Alterar turno
          </Botao>
        </div>
      ) : null}
    </section>
  );
}
